import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { DateTime } from 'luxon';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Collapse from '@mui/material/Collapse';
import Badge from '@mui/material/Badge';
import Chip from '@mui/material/Chip';
import { Theme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import MissionReportDurationPopover from './MissionReportDurationPopover';
import MissionInfo from './MissionInfo';
import StatusIcon from './StatusIcon';
import FiltersDialog from '../organizations/FiltersDialog';
import useFetchAll from '../../hooks/useFetchAll';
import { getCurrentPageName, trackEvent } from '../../services/analytics';
import { AnalyticsEvent } from '../../services/analytics-events';
import { describeFilters } from '../../services/analytics-filters';
import {
  CloseIcon,
  DownloadIcon,
  FirstPageArrow,
  LastPageArrow,
  OpenIcon,
  FilterIcon,
  DownArrow,
} from '@common/utils/icons/icons';
import TooltipIconButton from '@common/components/Inputs/TooltipIconButton';
import { useAppDispatch, useAppSelector } from '../../store';
import { selectMissionsHistoryData } from '../../store/robots/robots-slice-selectors';
import { selectCurrentOrganizationId } from '../../store/organization/organization-slice-selectors';
import { setSelectedMissionId } from '../../store/robots/robots-slice';
import { getMissionById, getMissions } from '../../store/robots/robots-actions';
import { convertSecondsToRoundedString, dateToStringFormat } from '@common/utils/date';
import { FilterKeys } from '@common/enums/query-params/filters';
import CustomAlert from '@common/components/Feedback/CustomAlert';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import Skeleton from '@mui/material/Skeleton';
import DistanceUnitSet from '@common/components/misc/DistanceUnitSet';
import { DistanceValueUnit } from '@common/enums/configuration/units';
import { useSharedSelector } from '@common/store/SharedProvider';
import { selectIsAdvancedModeEnabled } from '@common/store/app-configurations/app-configurations-selectors';
import MissionEndStatus from 'models/enums/mission-status';
import MissionList from 'models/missions/mission-list';

const MissionsHistory: React.FC = () => {
  const { t } = useTranslation();

  const matchesSmDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  const [filterParams, setFilterParams] = useSearchParams();
  const queryMissionId = filterParams.get(FilterKeys.MissionHistoryId);
  const queryRobotId = filterParams.get(FilterKeys.RobotId);
  const queryPage = filterParams.get(FilterKeys.Page);
  const { fetchAll: queryGetAllMissions, saveFetchAll, clearFetchAll } = useFetchAll('missions');
  const queryStartDay = filterParams.get(FilterKeys.StartDate)
    ? (DateTime.fromISO(filterParams.get(FilterKeys.StartDate)!, { zone: 'utc' })
        .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
        .toISO() ?? undefined)
    : undefined;
  const queryEndDay = filterParams.get(FilterKeys.EndDate)
    ? (DateTime.fromISO(filterParams.get(FilterKeys.EndDate)!, { zone: 'utc' })
        .set({ hour: 23, minute: 59, second: 59, millisecond: 999 })
        .toISO() ?? undefined)
    : undefined;

  const dispatch = useAppDispatch();

  const missionsData = useAppSelector((state) => selectMissionsHistoryData(state));
  const currentOrganizationId = useAppSelector((state) => selectCurrentOrganizationId(state));
  const isAdvancedModeEnabled = useSharedSelector((state) => selectIsAdvancedModeEnabled(state));

  const [reportRepeatAnchorEl, setReportRepeatAnchorEl] = React.useState<HTMLButtonElement | null>(
    null,
  );
  const [isFiltersDialogOpen, setIsFiltersDialogOpen] = useState(false);
  const [selectedMissionIndex, setSelectedMissionIndex] = useState<number | null>(null);

  useEffect(() => {
    if (queryMissionId !== missionsData.selectedId) {
      dispatch(
        setSelectedMissionId(
          queryMissionId == null || queryMissionId.length === 0 ? null : queryMissionId,
        ),
      );
      if (queryMissionId != null && queryMissionId.length > 0) {
        dispatch(getMissionById(queryMissionId));
      }
    }
  }, [dispatch, missionsData.selectedId, queryMissionId]);

  useEffect(() => {
    if (queryRobotId != null || currentOrganizationId != null) {
      dispatch(
        getMissions({
          org: queryRobotId ? undefined : currentOrganizationId,
          rid: queryRobotId ?? undefined,
          page: queryPage ?? '1',
          start: queryStartDay,
          end: queryEndDay,
          all: queryGetAllMissions,
        }),
      );
    }
  }, [
    currentOrganizationId,
    dispatch,
    queryEndDay,
    queryGetAllMissions,
    queryPage,
    queryRobotId,
    queryStartDay,
  ]);

  const selectMissionClickHandler = useCallback(
    (id: string) => {
      const index = missionsData.pagination?.data.findIndex((m) => m.id === id);
      if (id === queryMissionId || index == null || index === -1) {
        setSelectedMissionIndex((_) => {
          setFilterParams((prevState) => {
            prevState.delete(FilterKeys.MissionHistoryId);
            return prevState;
          });
          return null;
        });
      } else {
        setSelectedMissionIndex(index);
      }
    },
    [missionsData.pagination?.data, queryMissionId, setFilterParams],
  );

  const changePageClickHandler = useCallback(
    (direction?: -1 | 1, firstOrLastPage?: number) => {
      const page =
        direction != null
          ? (missionsData.pagination?.page ?? 1) + direction
          : firstOrLastPage
            ? firstOrLastPage
            : 1;
      setFilterParams((prevState) => {
        prevState.set(FilterKeys.Page, page.toString());
        prevState.delete(FilterKeys.MissionHistoryId);
        return prevState;
      });

      dispatch(
        getMissions({
          org: queryRobotId == null ? currentOrganizationId! : undefined,
          rid: queryRobotId ?? undefined,
          ps: '20',
          page: page.toString(),
          start: queryStartDay,
          end: queryEndDay,
          all: queryGetAllMissions,
        }),
      );
    },
    [
      missionsData.pagination?.page,
      setFilterParams,
      dispatch,
      queryRobotId,
      currentOrganizationId,
      queryStartDay,
      queryEndDay,
      queryGetAllMissions,
    ],
  );

  useEffect(() => {
    if (selectedMissionIndex == null || missionsData.pagination == null) return;

    if (selectedMissionIndex < 0) {
      if (missionsData.pagination!.page > 1) {
        changePageClickHandler(-1);
        setSelectedMissionIndex(19);
      } else {
        setSelectedMissionIndex(0);
      }
    } else if (selectedMissionIndex > missionsData.pagination.data.length - 1) {
      if (
        missionsData.pagination.page <
        Math.ceil(missionsData.pagination.count / missionsData.pagination.pageSize)
      ) {
        changePageClickHandler(1);
        setSelectedMissionIndex(0);
      } else {
        setSelectedMissionIndex(missionsData.pagination.data.length - 1);
      }
    } else if (
      selectedMissionIndex >= 0 &&
      selectedMissionIndex < missionsData.pagination.data.length
    ) {
      const missionFromIndex = missionsData.pagination.data[selectedMissionIndex];
      setFilterParams((prevState) => {
        prevState.set(FilterKeys.MissionHistoryId, missionFromIndex.id);
        return prevState;
      });
    }
  }, [changePageClickHandler, missionsData.pagination, selectedMissionIndex, setFilterParams]);

  const nextClickHandler = useCallback(() => {
    if (
      missionsData.pagination != null &&
      missionsData.pagination.page * missionsData.pagination.pageSize <
        missionsData.pagination.count
    ) {
      changePageClickHandler(1);
    }
  }, [missionsData.pagination, changePageClickHandler]);

  const prevClickHandler = useCallback(() => {
    if (missionsData.pagination != null && missionsData.pagination.page !== 1) {
      changePageClickHandler(-1);
    }
  }, [missionsData.pagination, changePageClickHandler]);

  const downClickHandler = useCallback(() => {
    setSelectedMissionIndex((prevState) => (prevState == null ? 0 : prevState + 1));
  }, []);

  const upClickHandler = useCallback(() => {
    setSelectedMissionIndex((prevState) => (prevState == null ? 19 : prevState - 1));
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        prevClickHandler();
      }
      if (event.key === 'ArrowRight') {
        nextClickHandler();
      }

      if (event.key === 'ArrowDown') {
        downClickHandler();
      }
      if (event.key === 'ArrowUp') {
        upClickHandler();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [prevClickHandler, nextClickHandler, downClickHandler, upClickHandler]);

  const openFiltersDialogClickHandler = () => {
    setIsFiltersDialogOpen(true);
  };

  const closeFiltersDialogHandler = () => {
    setIsFiltersDialogOpen(false);
  };

  const trackFiltersChanged = (
    filterAction: string,
    filters: { start?: string | null; end?: string | null; all?: boolean },
  ) => {
    const trackedFilters = queryRobotId == null ? filters : { ...filters, all: undefined };
    trackEvent(AnalyticsEvent.FiltersChanged, {
      ...describeFilters(trackedFilters, 'start', 'end'),
      filter_action: filterAction,
      source_page: getCurrentPageName(),
    });
  };

  const resetFiltersHandler = () => {
    trackFiltersChanged('reset', {});
    clearFetchAll();
    setFilterParams((prevState) => {
      prevState.delete(FilterKeys.StartDate);
      prevState.delete(FilterKeys.EndDate);
      prevState.delete(FilterKeys.All);
      return prevState;
    });
  };

  const weekFilterChangeHandler = (week: { start?: DateTime; end?: DateTime }) => {
    trackFiltersChanged('date_range', {
      start: week.start?.toISO() ?? filterParams.get(FilterKeys.StartDate),
      end: week.end?.toISO() ?? filterParams.get(FilterKeys.EndDate),
      all: queryGetAllMissions,
    });
    setFilterParams((prevState) => {
      if (week.start) {
        prevState.set(FilterKeys.StartDate, week.start.toISO()!);
      }
      if (week.end) {
        prevState.set(FilterKeys.EndDate, week.end.toISO()!);
      }
      return prevState;
    });
  };

  const handleDeleteFilters = () => {
    trackFiltersChanged('clear_dates', { all: queryGetAllMissions });
    setFilterParams((prevState) => {
      prevState.delete(FilterKeys.StartDate);
      prevState.delete(FilterKeys.EndDate);
      return prevState;
    });
  };

  const openDownloadOptionClickHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    setReportRepeatAnchorEl(event.currentTarget);
  };

  const closeReportRepeatPopover = () => {
    setReportRepeatAnchorEl(null);
  };

  const toggleAllClickHandler = () => {
    trackFiltersChanged(queryGetAllMissions ? 'show_filtered' : 'show_all', {
      start: filterParams.get(FilterKeys.StartDate),
      end: filterParams.get(FilterKeys.EndDate),
      all: !queryGetAllMissions,
    });
    saveFetchAll(!queryGetAllMissions);
    setFilterParams((prevState) => {
      prevState.set(FilterKeys.All, (!queryGetAllMissions).toString());
      return prevState;
    });
  };

  const missionTransitionFilter = (m: MissionList) =>
    isAdvancedModeEnabled || !m.isTransition || m.missionEndStatus !== MissionEndStatus.SUCCEEDED;

  const pagesCount =
    missionsData.pagination?.count != null && missionsData.pagination.count > 0
      ? Math.ceil(missionsData.pagination.count / missionsData.pagination.pageSize)
      : 1;

  const lastClickHandler =
    missionsData.pagination != null && missionsData.pagination.page !== pagesCount
      ? () => changePageClickHandler(undefined, pagesCount)
      : null;

  const firstClickHandler =
    missionsData.pagination != null && missionsData.pagination.page !== 1
      ? () => changePageClickHandler(undefined, 1)
      : null;

  return (
    <React.Fragment>
      <Stack spacing={1}>
        <Stack direction="row" alignItems="center" spacing={1}>
          {queryStartDay && queryEndDay && (
            <Chip
              onClick={openFiltersDialogClickHandler}
              onDelete={handleDeleteFilters}
              label={`${dateToStringFormat(queryStartDay, true)} - ${dateToStringFormat(queryEndDay, true)}`}
              variant="outlined"
            />
          )}
          {queryGetAllMissions && queryRobotId == null && (
            <Chip
              onClick={openFiltersDialogClickHandler}
              onDelete={toggleAllClickHandler}
              label={t('input-label-fetch-all')}
              variant="outlined"
            />
          )}
          <TooltipIconButton
            tooltip={t('info-tooltip-open-filters')}
            icon={
              <Badge color="primary">
                <FilterIcon color="primary" />
              </Badge>
            }
            onClick={openFiltersDialogClickHandler}
          />
          <TooltipIconButton
            tooltip={t('info-tooltip-download-report')}
            onClick={openDownloadOptionClickHandler}
            icon={<DownloadIcon />}
            color="primary"
          />
        </Stack>
        {missionsData.rest.getStatus === HttpFetchStatus.Idle ||
        missionsData.rest.getStatus === HttpFetchStatus.Loading ? (
          <TableContainer>
            <Table sx={{ tableLayout: matchesSmDown ? 'fixed' : undefined, width: '100%' }}>
              <TableBody>
                {[1, 2, 3, 4, 5, 6].map((index) => (
                  <TableRow
                    key={index}
                    sx={(theme) => ({ borderBottom: `1px solid ${theme.palette.divider}` })}
                  >
                    {matchesSmDown ? (
                      <TableCell sx={{ p: 1.5 }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Skeleton variant="circular" width={18} height={18} />
                          <Stack spacing={0.25} flex={1} minWidth={0}>
                            <Skeleton variant="text" width="60%" />
                          </Stack>
                          <Stack spacing={0.25} alignItems="flex-end">
                            <Skeleton variant="text" width={70} />
                            <Skeleton variant="text" width={50} />
                          </Stack>
                        </Stack>
                      </TableCell>
                    ) : (
                      <React.Fragment>
                        <TableCell sx={(theme) => ({ width: theme.spacing(4) })}>
                          <Skeleton variant="circular" width={20} height={20} />
                        </TableCell>
                        <TableCell>
                          <Skeleton variant="text" />
                        </TableCell>
                        <TableCell>
                          <Skeleton variant="text" />
                        </TableCell>
                        <TableCell>
                          <Skeleton variant="text" />
                        </TableCell>
                        <TableCell>
                          <Skeleton variant="text" />
                        </TableCell>
                      </React.Fragment>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : missionsData.pagination?.data == null || missionsData.pagination.data.length === 0 ? (
          <CustomAlert
            severity="info"
            header={
              queryStartDay == null ? t('info-no-missions') : t('info-no-missions-with-filters')
            }
          />
        ) : (
          <TableContainer>
            <Table sx={{ tableLayout: matchesSmDown ? 'fixed' : undefined, width: '100%' }}>
              <TableBody>
                {[...(missionsData.pagination?.data ?? [])]
                  .filter((m) => missionTransitionFilter(m))
                  .sort(
                    (a, b) =>
                      DateTime.fromISO(b.startTime).toMillis() -
                      DateTime.fromISO(a.startTime).toMillis(),
                  )
                  .map((m) => (
                    <React.Fragment key={m.id}>
                      <TableRow
                        sx={(theme) => ({
                          borderBottom: `1px solid ${theme.palette.divider}`,
                          cursor: 'pointer',
                          backgroundColor:
                            m.id === missionsData.currentMission?.id
                              ? theme.palette.action.hover
                              : undefined,
                          ':hover': { backgroundColor: theme.palette.action.hover },
                        })}
                        onClick={() => selectMissionClickHandler(m.id)}
                      >
                        {matchesSmDown ? (
                          <TableCell sx={{ p: 1.5 }}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Box sx={{ '& svg': { fontSize: 18 } }}>
                                <StatusIcon status={m.missionEndStatus} />
                              </Box>
                              <Stack spacing={0.25} flex={1} minWidth={0}>
                                <Typography variant="body2" noWrap>
                                  {m.deployment.robotName}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" noWrap>
                                  {m.roomName}
                                </Typography>
                              </Stack>
                              <Stack spacing={0.25} alignItems="flex-end">
                                <Typography variant="caption" color="text.secondary" noWrap>
                                  {dateToStringFormat(m.startTime, false, m.timezone)}
                                </Typography>
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                  <Typography variant="caption" color="text.secondary">
                                    {convertSecondsToRoundedString(m.duration)}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    -
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    <DistanceUnitSet
                                      value={m.cleanedArea}
                                      valueUnit={DistanceValueUnit.METERS_SQUARED}
                                      round
                                    />
                                  </Typography>
                                </Stack>
                              </Stack>
                              <IconButton edge="end" size="small">
                                <DownArrow
                                  sx={{
                                    transform:
                                      m.id === missionsData.currentMission?.id
                                        ? 'rotate(180deg)'
                                        : undefined,
                                    transition: 'transform 0.4s ease-in-out',
                                  }}
                                />
                              </IconButton>
                            </Stack>
                          </TableCell>
                        ) : (
                          <React.Fragment>
                            <TableCell sx={{ textAlign: 'center', pt: 2.5, pl: 1 }}>
                              <StatusIcon status={m.missionEndStatus} />
                            </TableCell>
                            <TableCell>{m.deployment.robotName}</TableCell>
                            <TableCell>{m.deployment.organizationName}</TableCell>
                            <TableCell>{m.roomName}</TableCell>
                            <TableCell>
                              {dateToStringFormat(m.startTime, false, m.timezone)}
                            </TableCell>
                            <TableCell>{convertSecondsToRoundedString(m.duration)}</TableCell>
                            <TableCell>
                              <DistanceUnitSet
                                value={m.cleanedArea}
                                valueUnit={DistanceValueUnit.METERS_SQUARED}
                                round
                              />
                            </TableCell>
                            <TableCell sx={(theme) => ({ width: theme.spacing(4) })}>
                              <IconButton>
                                <DownArrow
                                  sx={{
                                    transform:
                                      m.id === missionsData.currentMission?.id
                                        ? 'rotate(180deg)'
                                        : undefined,
                                    transition: 'transform 0.4s ease-in-out',
                                  }}
                                />
                              </IconButton>
                            </TableCell>
                          </React.Fragment>
                        )}
                      </TableRow>

                      <TableRow>
                        <TableCell colSpan={matchesSmDown ? 1 : 8} sx={{ p: 0 }}>
                          <Collapse
                            in={m.id === missionsData.currentMission?.id}
                            timeout="auto"
                            unmountOnExit
                          >
                            <MissionInfo mission={missionsData.currentMission} />
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {missionsData.pagination?.count != null && missionsData.pagination?.count > 0 && (
          <Stack
            direction="row"
            justifyContent={matchesSmDown ? 'center' : 'flex-end'}
            alignItems="center"
            width="100%"
            spacing={matchesSmDown ? 1 : 2}
          >
            <IconButton
              disabled={firstClickHandler == null}
              onClick={firstClickHandler ?? undefined}
            >
              <FirstPageArrow fontSize="small" />
            </IconButton>
            <IconButton disabled={prevClickHandler == null} onClick={prevClickHandler ?? undefined}>
              <CloseIcon fontSize="small" />
            </IconButton>
            <Typography fontSize="small">
              {missionsData.pagination?.page ?? 1} / {pagesCount}
            </Typography>
            <IconButton disabled={nextClickHandler == null} onClick={nextClickHandler ?? undefined}>
              <OpenIcon fontSize="small" />
            </IconButton>
            <IconButton disabled={lastClickHandler == null} onClick={lastClickHandler ?? undefined}>
              <LastPageArrow fontSize="small" />
            </IconButton>
          </Stack>
        )}
      </Stack>
      <FiltersDialog
        open={isFiltersDialogOpen}
        onClose={closeFiltersDialogHandler}
        onResetFilter={resetFiltersHandler}
        weekFiltersHandler={{
          weekFilter: {
            start: queryStartDay != null ? DateTime.fromISO(queryStartDay) : null,
            end: queryEndDay != null ? DateTime.fromISO(queryEndDay) : null,
          },
          onWeekFilterChange: weekFilterChangeHandler,
        }}
        allHandler={
          queryRobotId == null
            ? { all: queryGetAllMissions, onToggle: toggleAllClickHandler }
            : undefined
        }
      />
      <MissionReportDurationPopover
        anchorEl={reportRepeatAnchorEl}
        onClose={closeReportRepeatPopover}
        robotId={queryRobotId ?? undefined}
        organizationId={queryRobotId == null ? currentOrganizationId : undefined}
      />
    </React.Fragment>
  );
};

export default MissionsHistory;
